import { useEffect, useState } from "react";
import { Gift, Users, TrendUp, Coins, ShareNetwork, CheckCircle, Clock, Trophy, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card, Loading } from "@/components/riq";
import { toast } from "sonner";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

export default function AdminReferrals() {
  const [data, setData] = useState(null);
  const [reversing, setReversing] = useState(false);
  const [reversingRejected, setReversingRejected] = useState(false);

  const load = () => api.get("/admin/referrals").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const reverseDuplicates = async () => {
    if (!window.confirm("Reverse all duplicate referral rewards? This claws back Moolas from referrers (and the referred users) for any email that was rewarded more than once. The earliest legit reward per email is kept.")) return;
    setReversing(true);
    try {
      const { data: res } = await api.post("/admin/referrals/reverse-duplicates");
      toast.success(res.reversed ? `Reversed ${res.reversed} duplicate reward${res.reversed === 1 ? "" : "s"} · clawed back ${res.moolas_clawed_back} Moolas` : "No duplicate rewards found to reverse");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reverse duplicates.");
    } finally { setReversing(false); }
  };

  const reverseRejected = async () => {
    if (!window.confirm("Recalculate & claw back rewards for referrals whose referred user has NO valid (non-rejected) receipt? This removes Moolas from both the referrer and the referred user, and reverts the referral to pending so a genuine future scan can re-qualify it.")) return;
    setReversingRejected(true);
    try {
      const { data: res } = await api.post("/admin/referrals/reverse-rejected");
      toast.success(res.reverted ? `Reversed ${res.reverted} referral${res.reverted === 1 ? "" : "s"} · clawed back ${res.moolas_clawed_back} Moolas` : "No referrals to claw back — all qualified users have a valid receipt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not recalculate rewards.");
    } finally { setReversingRejected(false); }
  };

  if (data === false) return <div className="text-muted-foreground">Could not load referral data.</div>;
  if (!data) return <Loading />;

  return (
    <div className="space-y-6" data-testid="admin-referrals">
      {data.suspected_duplicates > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3" data-testid="reverse-duplicates-banner">
          <div className="text-sm text-red-700">
            <b>{data.suspected_duplicates}</b> suspected duplicate referral reward{data.suspected_duplicates === 1 ? "" : "s"} detected.
          </div>
          <button data-testid="reverse-duplicates-btn" onClick={reverseDuplicates} disabled={reversing}
            className="rounded-full bg-red-500 text-white px-4 py-2 text-xs font-semibold disabled:opacity-50 shrink-0">
            {reversing ? "Reversing…" : "Reverse duplicate rewards"}
          </button>
        </div>
      )}
      {data.reversible_rejected > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3" data-testid="reverse-rejected-banner">
          <div className="text-sm text-orange-800">
            <b>{data.reversible_rejected}</b> rewarded referral{data.reversible_rejected === 1 ? "" : "s"} where the new user has <b>no valid receipt</b> (rejected / fraudulent first scan).
          </div>
          <button data-testid="reverse-rejected-btn" onClick={reverseRejected} disabled={reversingRejected}
            className="rounded-full bg-orange-500 text-white px-4 py-2 text-xs font-semibold disabled:opacity-50 shrink-0">
            {reversingRejected ? "Recalculating…" : "Recalculate & claw back"}
          </button>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric icon={ShareNetwork} label="Total invites" value={data.total} />
        <Metric icon={CheckCircle} label="Qualified" value={data.qualified} tone="green" />
        <Metric icon={Clock} label="Pending scan" value={data.pending} tone="gold" />
        <Metric icon={TrendUp} label="Conversion" value={`${data.conversion_pct}%`} />
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <Metric icon={Users} label="Referred users" value={data.referred_users} sub={`${data.adoption_pct}% of all ${data.total_users} users`} />
        <Metric icon={Coins} label="Moolas paid out" value={data.moolas_paid} tone="gold" />
        <Metric icon={data.suspected_duplicates ? WarningCircle : Gift} label="Suspected duplicates"
          value={data.suspected_duplicates ?? 0} sub={data.same_ip_count ? `${data.same_ip_count} same-IP signups` : "same email reused"}
          tone={data.suspected_duplicates ? "red" : undefined} />
      </div>

      {/* Top referrers */}
      <Card data-testid="admin-top-referrers">
        <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2 mb-4"><Trophy size={18} weight="duotone" /> Top referrers</h3>
        {data.top_referrers.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2">No referrals yet.</div>
        ) : (
          <div className="divide-y divide-riq-border">
            {data.top_referrers.map((r, i) => (
              <div key={i} data-testid={`top-referrer-${i}`} className="flex items-center gap-3 py-2.5">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i < 3 ? "bg-riq-gold text-white" : "bg-secondary text-riq-primary"}`}>{i + 1}</span>
                <span className="flex-1 min-w-0 truncate text-sm font-medium">{r.email}</span>
                <span className="text-xs text-muted-foreground">{r.qualified}/{r.total} qualified</span>
                <span className="text-xs font-semibold text-riq-gold w-16 text-right shrink-0">{r.moolas} 🪙</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Recent referrals */}
      <Card data-testid="admin-recent-referrals">
        <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2 mb-4"><ShareNetwork size={18} weight="duotone" /> Recent referrals</h3>
        {data.recent.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2">No referrals yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-riq-border">
                  <th className="py-2 pr-3 font-semibold">Referrer</th>
                  <th className="py-2 pr-3 font-semibold">New user</th>
                  <th className="py-2 pr-3 font-semibold">Status</th>
                  <th className="py-2 pr-3 font-semibold">Invited</th>
                  <th className="py-2 pr-3 font-semibold text-right">Reward</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-riq-border">
                {data.recent.map((r, i) => (
                  <tr key={i} data-testid={`referral-row-${i}`} className={r.duplicate || r.no_receipt ? "bg-red-50/60" : ""}>
                    <td className="py-2.5 pr-3 truncate max-w-[200px]">{r.referrer_email}</td>
                    <td className="py-2.5 pr-3 truncate max-w-[200px] text-neutral-700">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate">{r.referred_email}</span>
                        {r.duplicate && <span data-testid={`dup-badge-${i}`} className="shrink-0 text-[10px] font-bold rounded-full px-1.5 py-0.5 bg-red-100 text-red-600">DUP</span>}
                        {r.same_ip && <span className="shrink-0 text-[10px] font-bold rounded-full px-1.5 py-0.5 bg-orange-100 text-orange-600">SAME IP</span>}
                        {r.no_receipt && <span data-testid={`noreceipt-badge-${i}`} className="shrink-0 text-[10px] font-bold rounded-full px-1.5 py-0.5 bg-orange-100 text-orange-700">NO RECEIPT</span>}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${r.status === "qualified" ? "bg-[#E4F7ED] text-[#22B573]" : r.status === "void_duplicate" ? "bg-red-100 text-red-600" : r.reverted ? "bg-orange-100 text-orange-700" : "bg-riq-gold/25 text-[#B25E00]"}`}>
                        {r.status === "qualified" ? "Rewarded" : r.status === "void_duplicate" ? "Blocked (dup)" : r.reverted ? "Reversed" : "Pending scan"}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="py-2.5 pr-3 text-right font-semibold text-riq-gold whitespace-nowrap">{r.reward ? `${r.reward} 🪙` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, tone }) {
  const toneMap = { green: "text-[#22B573]", gold: "text-riq-gold", red: "text-red-500" };
  return (
    <Card className="py-4">
      <div className="flex items-center gap-1.5 text-muted-foreground text-xs font-semibold uppercase tracking-wide">
        <Icon size={14} weight="duotone" className={toneMap[tone] || "text-riq-primary"} /> {label}
      </div>
      <div className="font-head text-2xl font-bold text-riq-primary mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}
